import React, { useEffect, useRef, useState } from 'react'
import CurrentStep from '../step/CurrentStep'
import CampaignGig from '../components/CampaignGig';
import PropertiesPreview from '../components/PropertiesPreview';
import { MilestoneStep, MilestoneEvent, Milestone, Gig } from 'types/campaign/collaborations';
import { SelectedStore } from '../gigs/Stores';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Swal from 'sweetalert2';
import { campaignIconSVG } from 'pages/Campaigns/Campaign';
import { toast } from 'react-toastify';
import { acknowledgeStep } from 'services/api/campaign/collaboration';

const MAX_HEIGHT = 135;

interface TimelineProps {
    active_steps: MilestoneStep[],
    events: MilestoneEvent[],
    milestone: Milestone,
    setOpenedStep: (slug: string) => void;
    onFeedbackView: (feedback: string) => void,
    addDeadline: (step: MilestoneStep) => void,
    onPlayBtnClick: (gig: Gig) => void
    setSelectedMedia: (media: any) => void
    onStepProcess?: () => void,
    refetchCollaboration?: () => void
}

interface TimelineEventProps {
    event: MilestoneEvent,
    getCompletedTaskComponent: (step: MilestoneStep, deliverables: any) => React.ReactNode
    onFeedbackView: (feedback: string) => void,
    refetchCollaboration?: () => void
}

const TimelineEvent: React.FC<TimelineEventProps> = ({ event, getCompletedTaskComponent, onFeedbackView, refetchCollaboration }) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { campaignId, collaborationId, } = useParams();
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            setIsOverflowing(el.scrollHeight > MAX_HEIGHT);
        });

        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    const acknowledge = async () => {
        if (!campaignId || !collaborationId || !selectedAgencyId || !event.step?.gig_step) return;

        try {
            await acknowledgeStep(selectedAgencyId, campaignId, collaborationId, event.step.gig_step.slug);
            refetchCollaboration?.();
        } catch (error: any) {
            console.log(error);
        }
    }

    return (
        <div className='position-relative'>
            <div className={`step-card step-card-completed`}>
                <div ref={contentRef} className={`step-card-2 timeline-event ${isExpanded ? 'expanded' : ''}`}>
                    <div className='d-flex flex-column'>
                        <span className={`${event.revision_required ? 'text-danger' : 'pop'} gig-step-title`}>{event.step.gig_step.processed_title ?? (event.step.gig_step.title ?? (event.step.gig_step.name ?? '')) + '*'}</span>
                        <span className='processed-at-timestamp'>{event.created}</span>
                        {
                            event.feedback?.text &&
                            <button onClick={() => onFeedbackView(event?.feedback?.text ?? "")} className="theme-btn theme-btn-sm theme-btn-primary-outlined mt-2 w-100 w-lg-25">View Feedback</button>
                        }
                        {
                            getCompletedTaskComponent(event.step, event.deliverables)
                        }
                    </div>
                    {!isExpanded && isOverflowing && <div className="fade-overlay" />}

                </div>
                {isOverflowing && (
                    <div
                        className="read-more-button p-2 px-3 m-0"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Show less' : 'Show more'}
                    </div>
                )}
                {
                    event.feedback?.reaction &&
                    <div className="emoji-bubble">
                        {
                            event.feedback.reaction === 'good' ? '👍' : (event.feedback.reaction === 'better' ? '❤️' : '🔥️')
                        }
                    </div>
                }
            </div>
            {
                event.step.gig_step.require_acknowledgement && !event.step.acknowledged_at &&
                <div className={`add-step-deadline`} style={{ marginLeft: '35px' }}>
                    <span>Acknowledge</span>
                    <button className='add-deadline-action unset-all cursor-pointer' onClick={() => acknowledge()}>
                        Yes
                    </button>
                </div>
            }
            {
                event.step.acknowledged_at &&
                <div className='add-step-deadline' style={{ marginLeft: '35px' }}>
                    <span>Acknowledged on {moment.utc(event.step.acknowledged_at).local().format('MMM DD, hh:mm A')} </span>
                </div>
            }
            {
                event.revision_required ?
                    (
                        <div className='rejected-icon'>
                            <i className='bx bx-x text-white fs-5'></i>
                        </div>
                    ) :
                    (
                        <div className='completed-icon'>
                            <i className='bx bx-check text-white fs-5' ></i>
                        </div>
                    )
            }

        </div>
    )
}

const Timeline: React.FC<TimelineProps> = ({ active_steps, events, milestone, setOpenedStep, onFeedbackView, addDeadline, onPlayBtnClick, setSelectedMedia, onStepProcess, refetchCollaboration }) => {
    const { campaignId, collaborationId, milestoneId } = useParams();
    const collaborationData = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.collaborations?.[collaborationId!]);

    const collaboration = collaborationData?.collaboration ?? null;

    const getCompletedTaskComponent = (completedTask: MilestoneStep, deliverables: any) => {
        if (['gig_selection', 'gig_addition'].includes(completedTask.gig_step.slug)) {

            if (milestone?.gig_type?.slug === 'store_visit') {
                return <SelectedStore gigs={milestone?.campaign_gigs} />
            }

            return (
                <div>
                    <div className="gigs-list">
                        {
                            milestone?.campaign_gigs.map((gig, i) => {
                                return (
                                    <CampaignGig gig={gig} gigType={milestone.gig_type.slug} onPlayBtnClick={onPlayBtnClick} />
                                )
                            })
                        }
                    </div>
                </div>
            )
        } else {
            return <PropertiesPreview setSelectedMedia={setSelectedMedia} files={Array.isArray(milestone?.files) ? milestone.files : []} deliverables={deliverables} step={completedTask} />
        }

        return null;
    }

    return (
        <div className="d-flex flex-column gap-3 position-relative">
            <div className="timeline-line"></div>
            {
                [...active_steps].reverse().map((currentTask, i) => {
                    return (
                        <CurrentStep
                            onFeedbackView={onFeedbackView}
                            feedbacks={milestone?.feedbacks ?? []}
                            currentStep={currentTask}
                            setOpenedStep={setOpenedStep}
                            addDeadline={() => addDeadline(currentTask)}
                            key={i}
                            onProcess={onStepProcess}
                            setSelectedMedia={setSelectedMedia}
                        />
                    )
                })
            }
            {
                active_steps.length === 0 && milestone?.ended_at &&
                <div>
                    <div className='step-card'>
                        <div className="step-card-2">
                            <div className='d-flex flex-column'>
                                <span className='pop gig-step-title'>Milestone Completed</span>
                                <span className='processed-at-timestamp'>{milestone?.ended_at}</span>
                            </div>
                        </div>
                    </div>

                </div>
            }
            {
                [...events].reverse().map((event, i) => {
                    return (
                        <TimelineEvent
                            key={i}
                            event={event}
                            getCompletedTaskComponent={getCompletedTaskComponent}
                            onFeedbackView={onFeedbackView}
                            refetchCollaboration={refetchCollaboration}
                        />
                    )
                })
            }
            <div className='position-relative'>
                <div className="collaborated-card step-card-completed">
                    <h6>Collaborated</h6>
                    <span className='processed-at-timestamp'>{milestone?.starts_at}</span>
                    {
                        collaboration?.language &&
                        <div>
                            <strong>Language: </strong>
                            <span>{collaboration.language.name}</span>
                        </div>
                    }
                </div>
                <div className='completed-icon'>
                    <i className='bx bx-check text-white fs-5' ></i>
                </div>
            </div>
        </div>
    )
}

export default Timeline